import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { dimensionShort, rubricColor } from "../lib/scoring";

const DIM_ORDER = [
  "optimism", "emotional_regulation", "ownership", "flexibility",
  "social_support", "purpose", "grit",
];

const COHORTS = [
  {
    key: "developing-5plus",
    label: "Developing 5+",
    description:
      "Respondents reading Developing on five or more of the seven dimensions. The could-go-either-way population — not failing on any single dimension, but carrying strain across many.",
    withWithinBand: true,
  },
  {
    key: "at-risk",
    label: "At-Risk",
    description:
      "Respondents reading At Risk on at least one dimension. The immediate-attention population.",
    withWithinBand: false,
  },
];

const WITHIN_BAND_COLORS = {
  Flat:     { bg: "#F0F7F4", fg: "#22645A", solid: "#4592A8" },
  Moderate: { bg: "#FAF5E8", fg: "#7A5D2F", solid: "#C99844" },
  Spiky:    { bg: "#FBEDED", fg: "#7A2629", solid: "#AD393E" },
};

/**
 * Sprint 5 — Cohort views.
 *
 * Two cohorts under one tab. Toggle between Developing 5+ and At-Risk.
 * Each cohort renders a per-respondent table with anon-ID, demographics,
 * per-dimension rubric chips, and (for Developing 5+) a within-band
 * variance classification. CSV export button per cohort.
 *
 * Two suppression rules apply:
 *   - Deployment n<5 → entire view collapses to a suppression panel.
 *   - Cohort n<5 → the count is shown but the member list is withheld.
 */
export default function Cohorts({ deploymentId, urlCohort, onSetCohort }) {
  const cohortKey = (urlCohort && COHORTS.find((c) => c.key === urlCohort)?.key) || "developing-5plus";
  const cohort = COHORTS.find((c) => c.key === cohortKey);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data: payload } = await api.get(
          `/api/deployments/${deploymentId}/cohorts/${cohortKey}`,
        );
        if (!cancelled) setData(payload);
      } catch (e) {
        if (!cancelled) setError(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [deploymentId, cohortKey]);

  function downloadCsv() {
    const url = `${api.defaults.baseURL || ""}/api/deployments/${deploymentId}/cohorts/${cohortKey}.csv`;
    // Sprint-10 Phase C — cookie-only auth. The browser attaches the
    // HttpOnly access_token cookie via credentials: 'include'; the old
    // localStorage Bearer fallback is gone.
    fetch(url, { credentials: "include" })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `${cohortKey}-${deploymentId.slice(0, 8)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
      });
  }

  return (
    <div data-testid="cohorts">
      <CohortsHeader />
      <CohortPicker
        cohorts={COHORTS}
        active={cohortKey}
        onChange={onSetCohort}
        onDownload={downloadCsv}
      />
      <CohortDescription cohort={cohort} />

      {loading && <div className="text-sm text-gray-500" data-testid="cohort-loading">Loading…</div>}
      {error && (
        <div className="text-sm" style={{ color: "#AD393E" }} data-testid="cohort-error">{error}</div>
      )}
      {data && <CohortBody data={data} cohort={cohort} />}
    </div>
  );
}

function CohortsHeader() {
  return (
    <div data-testid="cohorts-header">
      <div className="eyebrow mb-1">Cohorts</div>
      <h3 className="text-lg mb-3" style={{ fontFamily: "Inter", fontWeight: 500 }}>
        The people behind the numbers.
      </h3>
    </div>
  );
}

function CohortPicker({ cohorts, active, onChange, onDownload }) {
  return (
    <div className="flex items-center gap-3 mt-6 mb-4 flex-wrap" data-testid="cohort-picker">
      <div className="inline-flex border border-gray-200" role="tablist">
        {cohorts.map((c) => {
          const isActive = c.key === active;
          return (
            <button
              key={c.key}
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(c.key)}
              className="px-4 py-2 text-sm"
              style={{
                background: isActive ? "#1A1A1A" : "transparent",
                color: isActive ? "#FBFAF7" : "#1A1A1A",
                fontFamily: "Oswald",
                letterSpacing: "0.05em",
                borderRight: c.key !== cohorts[cohorts.length - 1].key ? "1px solid #E5E7EB" : "none",
              }}
              data-testid={`cohort-tab-${c.key}`}
            >
              {c.label}
            </button>
          );
        })}
      </div>
      <button
        onClick={onDownload}
        className="text-xs px-3 py-2 border border-gray-300"
        style={{ fontFamily: "Oswald", letterSpacing: "0.1em" }}
        data-testid="cohort-download-csv"
      >
        Download CSV
      </button>
    </div>
  );
}

function CohortDescription({ cohort }) {
  return (
    <p className="text-sm text-gray-700 max-w-prose mb-8" data-testid="cohort-description">
      {cohort.description}
    </p>
  );
}

function CohortBody({ data, cohort }) {
  if (data.suppressed) {
    return (
      <div className="panel" data-testid="cohort-deployment-suppressed">
        <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>Suppressed</div>
        <p className="text-sm text-gray-700">
          Only {data.n} completed respondent{data.n === 1 ? "" : "s"} in this deployment.
          Cohort views become available at five completed respondents.
        </p>
      </div>
    );
  }

  if (data.cohort_n === 0) {
    return (
      <div className="panel" data-testid="cohort-empty">
        <div className="eyebrow mb-2" style={{ color: "#2F6B7C" }}>Empty cohort</div>
        <p className="text-sm text-gray-700 max-w-prose">
          {cohort.key === "developing-5plus"
            ? "No respondent in this deployment is reading Developing on five or more dimensions. The cohort is empty."
            : "No respondent in this deployment is reading At Risk on any dimension. The cohort is empty."
          } That itself is a finding.
        </p>
        <p className="text-xs text-gray-500 mt-2">Deployment n = {data.deployment_n}</p>
      </div>
    );
  }

  if (data.list_suppressed) {
    return (
      <div className="panel" data-testid="cohort-list-suppressed">
        <div className="eyebrow mb-2" style={{ color: "#AD393E" }}>List withheld</div>
        <p className="text-sm text-gray-700 max-w-prose">
          {data.cohort_n} respondent{data.cohort_n === 1 ? "" : "s"} sit
          {data.cohort_n === 1 ? "s" : ""} in this cohort, out of {data.deployment_n}.
          The member list itself is withheld at this size — five or fewer members in a
          cohort can be triangulated by a reader with prior team knowledge. The count
          is reported; the names are not.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="cohort-table">
      <CountSummary data={data} cohortKey={cohort.key} />
      <MembersTable members={data.members} withWithinBand={cohort.withWithinBand} cohortKey={cohort.key} />
      <DisciplineFooter notes={[data.discipline_note, data.anonymization_note]} />
    </div>
  );
}

function CountSummary({ data, cohortKey }) {
  return (
    <div className="flex items-baseline gap-4 mb-4" data-testid="cohort-count-summary">
      <div style={{ fontFamily: "Oswald", fontWeight: 700, fontSize: "2rem", color: "#1A1A1A" }}>
        {data.cohort_n}
      </div>
      <div className="text-sm text-gray-700">
        of {data.deployment_n} respondents{" "}
        {cohortKey === "developing-5plus"
          ? "read Developing on five or more dimensions"
          : "read At Risk on at least one dimension"}.
      </div>
    </div>
  );
}

function MembersTable({ members, withWithinBand, cohortKey }) {
  // Discover the demographic keys actually present in this cohort.
  const demoKeys = useMemo(() => {
    const s = new Set();
    members.forEach((m) => Object.keys(m.demographics || {}).forEach((k) => s.add(k)));
    return Array.from(s).sort();
  }, [members]);

  return (
    <div className="overflow-x-auto border border-gray-100" data-testid="cohort-members-table">
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "#FAF8F2" }}>
            <Th>Anon ID</Th>
            {demoKeys.map((k) => (
              <Th key={k}>{k}</Th>
            ))}
            {DIM_ORDER.map((d) => (
              <Th key={d}>{dimensionShort(d)}</Th>
            ))}
            <Th align="right">
              {cohortKey === "developing-5plus" ? "Dev" : "At Risk"}
            </Th>
            {withWithinBand && <Th>Within-band</Th>}
          </tr>
        </thead>
        <tbody>
          {members.map((m) => (
            <MemberRow
              key={m.anon_id}
              m={m}
              demoKeys={demoKeys}
              withWithinBand={withWithinBand}
              cohortKey={cohortKey}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return (
    <th
      className="px-3 py-3 border-b border-gray-200"
      style={{
        textAlign: align,
        fontFamily: "Oswald",
        letterSpacing: "0.1em",
        color: "#666",
        fontWeight: 500,
        fontSize: "0.7rem",
        textTransform: "uppercase",
      }}
    >
      {children}
    </th>
  );
}

function MemberRow({ m, demoKeys, withWithinBand, cohortKey }) {
  const count = cohortKey === "developing-5plus" ? m.developing_count : m.at_risk_count;
  return (
    <tr className="border-t border-gray-100" data-testid={`cohort-row-${m.anon_id}`}>
      <td className="px-3 py-3" style={{ fontFamily: "Oswald", fontWeight: 600, color: "#1A1A1A" }}>
        {m.anon_id}
      </td>
      {demoKeys.map((k) => (
        <td key={k} className="px-3 py-3 text-gray-700">{(m.demographics || {})[k] || "—"}</td>
      ))}
      {DIM_ORDER.map((d) => (
        <td key={d} className="px-3 py-3">
          <RubricChip level={m.rubric_levels[d]} />
        </td>
      ))}
      <td
        className="px-3 py-3 text-right"
        style={{ fontFamily: "Oswald", fontWeight: 600, color: count >= 5 ? "#AD393E" : "#1A1A1A" }}
      >
        {count}
      </td>
      {withWithinBand && (
        <td className="px-3 py-3">
          <WithinBandChip cls={m.within_band_class} sd={m.within_band_sd} />
        </td>
      )}
    </tr>
  );
}

function RubricChip({ level }) {
  if (!level) return <span className="text-xs text-gray-400">—</span>;
  const c = rubricColor(level);
  const short = { "At Risk": "AR", Emerging: "EM", Developing: "DV", Strong: "ST" }[level] || level;
  return (
    <span
      className="inline-block px-2 py-0.5 text-xs"
      style={{
        background: c.bg,
        color: c.fg,
        fontFamily: "Oswald",
        letterSpacing: "0.05em",
      }}
      title={level}
      data-testid={`rubric-chip-${level.replace(/ /g, "-")}`}
    >
      {short}
    </span>
  );
}

function WithinBandChip({ cls, sd }) {
  if (!cls) return <span className="text-xs text-gray-400">—</span>;
  const c = WITHIN_BAND_COLORS[cls] || WITHIN_BAND_COLORS.Flat;
  return (
    <span
      className="inline-flex items-baseline gap-2 px-2 py-0.5 text-xs"
      style={{
        background: c.bg,
        color: c.fg,
        fontFamily: "Oswald",
        letterSpacing: "0.05em",
      }}
      title={`within-band σ = ${sd}`}
      data-testid={`within-band-chip-${cls}`}
    >
      {cls}
      <span className="text-gray-500" style={{ fontWeight: 400 }}>σ={sd}</span>
    </span>
  );
}

function DisciplineFooter({ notes }) {
  return (
    <div className="mt-6 space-y-2 max-w-prose" data-testid="cohort-discipline-footer">
      {notes.filter(Boolean).map((n, i) => (
        <p key={i} className="text-xs text-gray-500">{n}</p>
      ))}
    </div>
  );
}
